// medplum-rooms.js — ONLINE-PRIMARY (main-2) doctor SEATING / rooms (WS-S).
//
// THE MODEL. A "room" is a FHIR Location (physicalType room) keyed by a stable
// room-slug identifier and attached to its department the FHIR-correct way
// (Location.managingOrganization = the dept Organization). A doctor's SEAT is
// PractitionerRole.location[0] (reference + display "Room 12") — it rides the
// SAME PractitionerRole that carries availableTime/leave, so "it goes into their
// schedule" holds. This is deliberately the CURRENT seat (editable anytime, incl.
// by voice each morning); per-weekday seat maps are OUT of v1 (see DEFERRED.md).
//
// THE READS are RLS-scoped FHIR searches run as the logged-in doctor — Location
// and PractitionerRole are readonly-READABLE to every staff role, so listing rooms
// and reading one's own seat need no privilege.
//
// THE WRITES go through the PRIVILEGED `schedule-change` Bot
// (scripts/medplum/bots/schedule-change.cjs), $execute'd as the logged-in doctor.
// Under RLS a doctor's own token has Location AND PractitionerRole readonly, so a
// doctor can neither mint a room nor write their own (or anyone's) seat directly —
// exactly the reason availableTime/leave already route through this Bot. The Bot
// enforces authorization off the SERVER-SET event.requester: create-room /
// rename-room are HOD-or-admin (own department); set-room sets the caller's OWN
// seat (any active doctor) or a dept-mate's seat only when the caller strictly
// outranks them in the same department (or is an admin). Same door as the schedule
// change chain, same Bot id — this module just shapes the DTOs the screens read.
import { getMedplum, deriveCurrentUser, invalidateCached, loadRawConfig } from './client-port.js';
import { clinicTodayKey } from '@healther/clinical-core/clinic-time.js';

const ROOM_SLUG_SYSTEM = 'https://healther.io/opd/room-slug';
const ROOM_PHYS_SYSTEM = 'http://terminology.hl7.org/CodeSystem/location-physical-type';
const DEPT_SLUG_SYSTEM = 'https://healther.io/opd/department-slug';

const refId = (ref) => (ref && ref.reference ? String(ref.reference).split('/').pop() : '');

// ── PER-WEEKDAY ROOMS (room-by-day) — the resolver + its extension ───────────
// A doctor can sit in a DIFFERENT room on different weekdays. PractitionerRole
// .location[0] stays the DEFAULT room ("room everywhere"); an optional
// PractitionerRole extension https://healther.io/opd/room-by-day carries a
// valueString JSON map { mon:{id,name}, tue:{…}, … } of ONLY the days that differ
// from the default. effectiveRoom(role, date) is the ONE pure resolver every
// surface reads through (room-by-day[weekday] ?? location[0] ?? null) — a VERBATIM
// inline parity copy lives in scripts/medplum/bots/anon-saathi-finalize.cjs (the
// intake-guards parity idiom; verify-doctor-seating.mjs + the vitest suite assert
// the two match byte-for-byte). Edit BOTH copies together.
export const ROOM_BY_DAY_EXT = 'https://healther.io/opd/room-by-day';
const DAY_CODES_SUNFIRST = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/**
 * Weekday code (mon..sun) for a 'YYYY-MM-DD' clinic day, or '' when unparseable.
 * UTC-midnight getUTCDay — a calendar date has exactly one weekday worldwide, so
 * this is timezone-invariant (matches medplum-availability.js dayKeyOf).
 */
export function weekdayCode(dateISO) {
  const [y, mo, d] = String(dateISO || '').slice(0, 10).split('-').map(Number);
  if (!y || !mo || !d) return '';
  return DAY_CODES_SUNFIRST[new Date(Date.UTC(y, mo - 1, d)).getUTCDay()];
}

/** Parse the room-by-day extension → { mon:{id,name}, … } (always a plain object). */
export function readRoomByDay(role) {
  const ext = ((role && role.extension) || []).find((e) => e && e.url === ROOM_BY_DAY_EXT);
  if (!ext || typeof ext.valueString !== 'string') return {};
  let v;
  try { v = JSON.parse(ext.valueString); } catch (_) { return {}; }
  return (v && typeof v === 'object' && !Array.isArray(v)) ? v : {};
}

/**
 * The doctor's EFFECTIVE room for a clinic day: the weekday override, else the
 * default seat (location[0]), else null. Returns { id, name } | null. PURE.
 */
export function effectiveRoom(role, dateISO) {
  const byDay = readRoomByDay(role);
  const wd = weekdayCode(dateISO);
  const entry = wd ? byDay[wd] : null;
  if (entry && (entry.id || entry.name)) {
    return { id: entry.id ? String(entry.id) : '', name: entry.name ? String(entry.name) : '' };
  }
  const seat = (role && Array.isArray(role.location) ? role.location[0] : null);
  if (seat && (seat.reference || seat.display)) {
    return { id: seat.reference ? String(seat.reference).split('/').pop() : '', name: seat.display ? String(seat.display) : '' };
  }
  return null;
}

// executeBot wraps the handler return inconsistently ({returnValue} vs the value
// directly) — unwrap both (the medplum-approvals.js / medplum-staff.js pattern).
const unwrapBot = (out) => (out && out.returnValue) || out;

// Bot id lives at cfg.bots.scheduleChange (written by deploy-main2 CONFIG_BOT_NAMES).
async function scheduleChangeBotId() {
  const cfg = await loadRawConfig();
  const id = cfg && cfg.bots && cfg.bots.scheduleChange;
  if (!id) throw new Error('schedule-change Bot not configured (cfg.bots.scheduleChange)');
  return id;
}

// A Bot OperationOutcome (a refusal — e.g. the HOD guard) surfaces as an issue[]
// return when $execute succeeds with a 400 body. Throw the honest message so the
// caller's catch shows it verbatim.
function throwIfOutcome(r, fallback) {
  if (r && r.resourceType === 'OperationOutcome') {
    const issue = (r.issue || [])[0] || {};
    const msg = (issue.details && issue.details.text) || issue.diagnostics || fallback;
    throw new Error(msg);
  }
  return r;
}

async function execRoomBot(body, fallback) {
  const botId = await scheduleChangeBotId();
  const { medplum } = await getMedplum();
  const r = throwIfOutcome(unwrapBot(await medplum.executeBot(botId, body)), fallback);
  if (!r || r.ok !== true) throw new Error((r && r.error) || fallback);
  // Typed to what the Bot actually changed: create/rename-room mint or edit a
  // Location, set-room writes the seat onto a PractitionerRole. Schedule.jsx
  // re-lists rooms and MyRoomChip re-reads the seat straight after — both cached.
  // NOTE: my-rooms.jsx's invalidateAvailabilityCache() only nulls a module-local
  // promise in medplum-availability.js; it never touched MedplumClient's own GET
  // cache, which is why the seat could appear to save and then revert.
  await invalidateCached(body && body.action === 'set-room' ? 'PractitionerRole' : 'Location');
  return r;
}

// ── Location bundle → room DTO ──────────────────────────────────────────────
/** Shape a Location into { id, name, slug, departmentRef, departmentName }. Pure. */
export function shapeRoom(loc, orgNameById) {
  if (!loc || !loc.id) return null;
  const slug = ((loc.identifier || []).find((i) => i && i.system === ROOM_SLUG_SYSTEM) || {}).value || '';
  const departmentRef = (loc.managingOrganization && loc.managingOrganization.reference) || '';
  const orgId = refId(loc.managingOrganization);
  return {
    id: loc.id,
    name: loc.name || slug || 'Room',
    slug,
    departmentRef,
    departmentName: (loc.managingOrganization && loc.managingOrganization.display)
      || (orgNameById && orgId && orgNameById.get(orgId)) || '',
  };
}

/**
 * Every OPD room (physicalType room, keyed by the room-slug identifier), newest
 * name-sorted. Optional { departmentRef } filters to one department's rooms.
 * RLS-scoped read as the logged-in user; `_include`s the managingOrganization so
 * each room carries its department name without a second round trip.
 * @returns {Promise<Array<{ id, name, slug, departmentRef, departmentName }>>}
 */
export async function listRooms({ departmentRef } = {}) {
  const { medplum } = await getMedplum();
  const bundle = await medplum.search(
    'Location',
    `identifier=${encodeURIComponent(`${ROOM_SLUG_SYSTEM}|`)}&_include=Location:organization&_count=500`,
  );
  const orgNameById = new Map();
  const locs = [];
  for (const entry of (bundle && bundle.entry) || []) {
    const r = entry && entry.resource;
    if (!r) continue;
    if (r.resourceType === 'Organization' && r.id) orgNameById.set(r.id, r.name || '');
    else if (r.resourceType === 'Location') locs.push(r);
  }
  let rooms = locs.map((l) => shapeRoom(l, orgNameById)).filter(Boolean);
  if (departmentRef) rooms = rooms.filter((r) => r.departmentRef === departmentRef);
  rooms.sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { numeric: true }));
  return rooms;
}

/** The logged-in doctor's DEFAULT seat, or null. { roomId, roomName } from PractitionerRole.location[0]. */
export async function getMyRoom() {
  const { medplum } = await getMedplum();
  const me = await deriveCurrentUser(medplum);
  const role = await medplum.searchOne('PractitionerRole', `practitioner=Practitioner/${me.id}&active=true`);
  const seat = ((role && role.location) || [])[0] || null;
  if (!seat || !seat.reference) return null;
  return { roomId: refId(seat), roomName: seat.display || '' };
}

/**
 * The logged-in doctor's DEFAULT seat + per-weekday overrides, for the availability
 * editors. { defaultRoom:{ roomId, roomName }|null, roomByDay:{ mon:{id,name}, … } }.
 */
export async function getMyRooms() {
  const { medplum } = await getMedplum();
  const me = await deriveCurrentUser(medplum);
  const role = await medplum.searchOne('PractitionerRole', `practitioner=Practitioner/${me.id}&active=true`);
  const seat = ((role && role.location) || [])[0] || null;
  return {
    defaultRoom: seat && seat.reference ? { roomId: refId(seat), roomName: seat.display || '' } : null,
    roomByDay: readRoomByDay(role),
  };
}

/** The logged-in doctor's EFFECTIVE room TODAY (weekday override ?? default), or null. */
export async function getMyRoomToday(dateISO) {
  const { medplum } = await getMedplum();
  const me = await deriveCurrentUser(medplum);
  const role = await medplum.searchOne('PractitionerRole', `practitioner=Practitioner/${me.id}&active=true`);
  const r = effectiveRoom(role, dateISO || clinicTodayKey());
  return r && r.name ? { roomId: r.id, roomName: r.name } : null;
}

/** Add a room. HOD (own dept) or admin — the Bot enforces it. Admin may target
 *  a department by canonical Organization reference OR its stable department
 *  slug; the Bot resolves the slug. HOD input is ignored and forced to self. */
export function createRoom({ name, departmentRef, departmentSlug } = {}) {
  const body = { action: 'create-room', name: String(name || '') };
  if (departmentRef) body.departmentRef = String(departmentRef);
  if (departmentSlug) body.departmentSlug = String(departmentSlug);
  return execRoomBot(body, 'Could not add the room').then((r) => r.room);
}

/** Rename a room. HOD (own dept) or admin. */
export function renameRoom({ roomId, name } = {}) {
  return execRoomBot({ action: 'rename-room', roomId: String(roomId || ''), name: String(name || '') }, 'Could not rename the room')
    .then((r) => r.room);
}

/**
 * Set MY own seat (any active doctor). roomId '' / null clears it. With `day`
 * (mon..sun) it writes/clears that WEEKDAY's room-by-day override and leaves the
 * default (location[0]) untouched; without `day` it sets the DEFAULT ("everywhere").
 */
export function setMySeat({ roomId, day } = {}) {
  const body = { action: 'set-room', roomId: roomId ? String(roomId) : '' };
  if (day) body.day = String(day);
  return execRoomBot(body, 'Could not set your room');
}

/** Set a dept-mate's seat (HOD / outranking-same-dept / admin — the Bot enforces it). `day` writes that weekday's override. */
export function setDoctorSeat({ doctorId, roomId, day } = {}) {
  const body = { action: 'set-room', doctorId: String(doctorId || ''), roomId: roomId ? String(roomId) : '' };
  if (day) body.day = String(day);
  return execRoomBot(body, 'Could not set the room');
}

export const __roomsTest = {
  shapeRoom, effectiveRoom, weekdayCode, readRoomByDay,
  ROOM_SLUG_SYSTEM, ROOM_PHYS_SYSTEM, DEPT_SLUG_SYSTEM, ROOM_BY_DAY_EXT,
};
